import type { HomeyAPI, Notification } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { __DEV__ } from '../../lib/__dev__';

export type NotificationProps = {
  [K in keyof Notification as Notification[K] extends Function ? never : K]: Notification[K];
};

type NotificationsData = { [key: string]: Notification };
export type NotificationsById = { [key: string]: NotificationProps };

interface State {
  data: NotificationsData | null;
  byId: NotificationsById | null;
  isLoading: boolean;
  error: unknown;
}

export type NotificationsStoreState = State;

export class NotificationsStore extends BaseApiStore<State, Notification> {
  constructor() {
    super('notifications');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.notifications, reference: this });

      if (abortController.signal.aborted) return;

      const notificationsPromise = api.notifications.getNotifications({
        $cache: cache,
      });
      const [notificationsData] = await Promise.all([notificationsPromise]);

      if (abortController.signal.aborted) return;

      const notifications = Object.values(notificationsData);

      const data: NotificationsData = {};
      const byId: NotificationsById = {};

      for (const notificationInstance of notifications) {
        const notificationReference = { ...notificationInstance };

        data[notificationReference.id] = notificationInstance;
        byId[notificationReference.id] = notificationReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.notifications, reference: this }).catch(
        console.error
      );
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.notifications.on('notification.create', this.handleCreate);
    api.notifications.on('notification.update', this.handleUpdate);
    api.notifications.on('notification.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.notifications.off('notification.create', this.handleCreate);
    api.notifications.off('notification.update', this.handleUpdate);
    api.notifications.off('notification.delete', this.handleDelete);
  }

  getInstanceFromCache(notification: { id: string }) {
    return this.getCache()[notification.id];
  }

  getCache(): { [key: string]: Notification } {
    const api = this.getApi();
    const cache = api.notifications.__cache['notification'];
    return cache;
  }

  handleCreate = (createdNotification: Notification) => {
    this.DEBUG && console.info(`create:notification:${createdNotification.id}`);
    const state = this.get();
    const notificationInstance = this.getInstanceFromCache(createdNotification);

    if (notificationInstance == null) return;

    const notificationReference = { ...notificationInstance };

    this.set({
      data: {
        ...state.data,
        [createdNotification.id]: notificationInstance,
      },
      byId: {
        ...state.byId,
        [createdNotification.id]: notificationReference,
      },
    });
  };

  handleUpdate = (updatedNotification: Notification) => {
    this.DEBUG && console.info(`update:notification:${updatedNotification.id}`);

    this.addToQueue(updatedNotification.id, updatedNotification);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedNotification] of Object.entries(updateQueue)) {
        const notificationInstance = this.getInstanceFromCache(queuedNotification);

        if (state.data == null) {
          this.DEBUG && console.info(`update:notification:missing-data`);
          continue;
        }

        if (notificationInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:notification:unknown:${id}`);
          continue;
        }

        const notificationReference = { ...notificationInstance };

        nextById[id] = notificationReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:notification:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedNotification: Notification) => {
    this.DEBUG && console.info(`delete:notification:${deletedNotification.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:notification:missing-data`);
      return;
    }

    const notificationInstance = state.data[deletedNotification.id];

    if (notificationInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[notificationInstance.id];
    delete nextById[notificationInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const notifications = Object.values(cache ?? {});
    const data: NotificationsData = {};
    const byId: NotificationsById = {};

    for (const notificationInstance of notifications) {
      const notificationReference = { ...notificationInstance };
      data[notificationReference.id] = notificationInstance;
      byId[notificationReference.id] = notificationReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const notificationsStore = new NotificationsStore();
